import { workspacesApi } from "@/lib/api";
import { mergeTagsForAvatarSync, type WorkspaceAvatarSelection } from "@/lib/workspaceAvatar";

type WorkspaceTagRow = { id?: string; key: string; value: string };

export function unwrapWorkspaceTags(data: unknown): WorkspaceTagRow[] {
    if (Array.isArray(data)) {
        return data.map((t) => ({
            id: typeof (t as { id?: string })?.id === "string" ? (t as { id: string }).id : undefined,
            key: String((t as { key?: string })?.key ?? "").trim(),
            value: String((t as { value?: string })?.value ?? "").trim(),
        })).filter((t) => t.key.length > 0);
    }
    if (!data || typeof data !== "object") return [];
    const obj = data as { tags?: unknown; data?: unknown };
    const innerData =
        obj.data && typeof obj.data === "object" ? (obj.data as { tags?: unknown }) : null;
    const inner = obj.tags ?? innerData?.tags ?? null;
    return Array.isArray(inner) ? unwrapWorkspaceTags(inner) : [];
}

export async function fetchWorkspaceTags(
    workspaceId: string,
    fallback?: Array<{ id?: string; key?: string; value?: string }> | null,
): Promise<WorkspaceTagRow[]> {
    try {
        const raw = await workspacesApi.tags.list(workspaceId);
        return unwrapWorkspaceTags(raw);
    } catch {
        // Members used to 403 on tags.list — never wipe list/getById tags (avatar icon lives there).
        return unwrapWorkspaceTags(fallback ?? []);
    }
}

export async function syncWorkspaceAvatarTags(
    workspaceId: string,
    avatar: WorkspaceAvatarSelection,
    existingTags: WorkspaceTagRow[],
): Promise<void> {
    const findTag = (key: string) => existingTags.find((t) => t.key === key);

    const upsert = async (key: string, value: string) => {
        const existing = findTag(key);
        if (existing?.id) {
            if (existing.value === value) return;
            try {
                await workspacesApi.tags.update(workspaceId, existing.id, { value });
            } catch {
                // ignore no-op / duplicate
            }
            return;
        }
        try {
            await workspacesApi.tags.create(workspaceId, { key, value });
        } catch {
            // ignore duplicate
        }
    };

    const remove = async (key: string) => {
        const existing = findTag(key);
        if (!existing) return;
        try {
            await workspacesApi.tags.deleteByKey(workspaceId, key);
        } catch {
            // ignore
        }
    };

    if (avatar.mode === "icon") {
        await upsert("avatar_type", "icon");
        await upsert("icon_key", avatar.iconKey);
        await upsert("icon_color", avatar.color);
        return;
    }

    await upsert("avatar_type", "image");
    await remove("icon_key");
    await remove("icon_color");
}

export async function syncWorkspaceAvatarTagsFromWorkspace(
    workspaceId: string,
    avatar: WorkspaceAvatarSelection,
    businessTags: Array<{ id?: string; key: string; value: string }>,
    workspaceTags?: Array<{ id?: string; key?: string; value?: string }> | null,
): Promise<void> {
    return syncWorkspaceAvatarTags(
        workspaceId,
        avatar,
        mergeTagsForAvatarSync(businessTags, workspaceTags),
    );
}
